import { useState, useEffect } from "react";
import Button from "../../../components/base/Button";
import { postTestimonialData, getTestimonialData } from "../../../api/apiServices";

export default function TestimonialsEditor() {
  const [testimonialsData, setTestimonialsData] = useState({
    sectionTitle: "Client Success Stories",
    sectionSubtitle: "What my clients say about working with me",
    testimonials: [
      {
        clientName: "Sarah Johnson",
        position: "CEO, TechStart Inc.",
        avatar:
          "https://readdy.ai/api/search-image?query=Professional%20business%20woman%20CEO%20headshot%2C%20confident%20smile%2C%20business%20attire%2C%20corporate%20portrait%2C%20professional%20lighting%2C%20modern%20office%20background%2C%20executive%20photo&width=100&height=100&seq=avatar-1&orientation=squarish",
        testimonialContent:
          "Working with this developer was an absolute game-changer for our business. The website exceeded our expectations in every way - from design to functionality. Our conversion rate increased by 150% within the first month!",
        rating: 5,
        project: "E-commerce Platform",
      },
      {
        clientName: "Michael Chen",
        position: "Founder, Creative Studios",
        avatar:
          "https://readdy.ai/api/search-image?query=Professional%20Asian%20businessman%20headshot%2C%20friendly%20expression%2C%20modern%20business%20attire%2C%20creative%20industry%20professional%2C%20studio%20lighting%2C%20contemporary%20background&width=100&height=100&seq=avatar-2&orientation=squarish",
        testimonialContent:
          "The attention to detail and technical expertise is outstanding. Our new portfolio website has helped us land major clients and showcase our work in the best possible light. Highly recommended!",
        rating: 5,
        project: "Portfolio Website",
      },
    ],
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ message: "", type: "" });

  // ✅ Prefill data from backend on load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getTestimonialData();
        if (data && (data.sectionTitle || data.clientTestimonials?.length)) {
          setTestimonialsData({
            sectionTitle: data.sectionTitle || testimonialsData.sectionTitle,
            sectionSubtitle: data.sectionSubtitle || testimonialsData.sectionSubtitle,
            testimonials:
              data.clientTestimonials?.map((t) => ({
                clientName: t.clientName,
                position: t.position,
                company: t.company,
                avatar: t.avatar,
                testimonialContent: t.testimonialContent,
                rating: t.rating,
                featured: t.featured || false,
              })) || testimonialsData.testimonials,
          });
        }
      } catch (error) {
        console.error("❌ Failed to load testimonials:", error);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSectionChange = (field, value) => {
    setTestimonialsData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTestimonialChange = (index, field, value) => {
    setTestimonialsData((prev) => ({
      ...prev,
      testimonials: prev.testimonials.map((t, i) =>
        i === index ? { ...t, [field]: value } : t
      ),
    }));
  };

  const addTestimonial = () => {
    setTestimonialsData((prev) => ({
      ...prev,
      testimonials: [
        ...prev.testimonials,
        {
          clientName: "New Client",
          position: "Position",
          company: "Company Name",
          avatar:
            "Professional headshot, business attire, office background, confident expression, professional lighting",
          rating: 5,
          testimonialContent: "Testimonial content goes here...",
          featured: false,
        },
      ],
    }));
  };

  const removeTestimonial = (index) => {
    setTestimonialsData((prev) => ({
      ...prev,
      testimonials: prev.testimonials.filter((_, i) => i !== index),
    }));
  };

  // ✅ Submit testimonials to backend
  const handleSave = async () => {
    setLoading(true);
    setStatus({ message: "", type: "" });
    try {
      const payload = {
        sectionTitle: testimonialsData.sectionTitle,
        sectionSubtitle: testimonialsData.sectionSubtitle,
        clientTestimonials: testimonialsData.testimonials.map((t) => ({
          featured: t.featured,
          clientName: t.clientName,
          position: t.position,
          company: t.company,
          rating: t.rating,
          avatar: t.avatar,
          testimonialContent: t.testimonialContent,
        })),
      };

      const response = await postTestimonialData(payload);

      setStatus({ message: "✅ Testimonials saved successfully!", type: "success" });
      console.log("✅ Backend response:", response);
    } catch (error) {
      console.error("❌ Error posting testimonials:", error);
      setStatus({
        message: "Failed to save testimonials. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* --- Section Header --- */}
      <div>
        <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
          <i className="ri-chat-quote-line mr-3 text-emerald-600"></i>
          Testimonials Section Settings
        </h2>
      </div>

      <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
        <h3 className="text-xl font-bold text-slate-800 mb-4">Section Header</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-slate-700 font-semibold mb-3">
              Section Title
            </label>
            <input
              type="text"
              value={testimonialsData.sectionTitle}
              onChange={(e) => handleSectionChange("sectionTitle", e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
            />
          </div>
          <div>
            <label className="block text-slate-700 font-semibold mb-3">
              Section Subtitle
            </label>
            <input
              type="text"
              value={testimonialsData.sectionSubtitle}
              onChange={(e) => handleSectionChange("sectionSubtitle", e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
            />
          </div>
        </div>
      </div>

      {/* --- Testimonials List --- */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-slate-800">Client Testimonials</h3>
          <Button
            onClick={addTestimonial}
            size="sm"
            variant="outline"
            className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
          >
            <i className="ri-add-line mr-2"></i>
            Add Testimonial
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {testimonialsData.testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <h4 className="font-semibold text-slate-800">
                    Testimonial {index + 1}
                  </h4>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={testimonial.featured}
                      onChange={(e) =>
                        handleTestimonialChange(index, "featured", e.target.checked)
                      }
                      className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                    />
                    <span className="text-sm text-slate-600 font-medium">
                      Featured
                    </span>
                  </label>
                </div>
                {testimonialsData.testimonials.length > 1 && (
                  <button
                    onClick={() => removeTestimonial(index)}
                    className="text-red-500 hover:text-red-700 transition-colors duration-300"
                  >
                    <i className="ri-delete-bin-line text-lg"></i>
                  </button>
                )}
              </div>

              {/* --- Testimonial Fields --- */}
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-600 font-medium mb-2">
                      Client Name
                    </label>
                    <input
                      type="text"
                      value={testimonial.clientName}
                      onChange={(e) =>
                        handleTestimonialChange(index, "clientName", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium mb-2">
                      Position
                    </label>
                    <input
                      type="text"
                      value={testimonial.position}
                      onChange={(e) =>
                        handleTestimonialChange(index, "position", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-600 font-medium mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      value={testimonial.company}
                      onChange={(e) =>
                        handleTestimonialChange(index, "company", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium mb-2">
                      Rating
                    </label>
                    <select
                      value={testimonial.rating}
                      onChange={(e) =>
                        handleTestimonialChange(index, "rating", parseInt(e.target.value))
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    >
                      {[5, 4, 3, 2, 1].map((num) => (
                        <option key={num} value={num}>
                          {num} Star{num > 1 ? "s" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-slate-600 font-medium mb-2">
                    Avatar Prompt
                  </label>
                  <textarea
                    value={testimonial.avatar}
                    onChange={(e) =>
                      handleTestimonialChange(index, "avatar", e.target.value)
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-600 font-medium mb-2">
                    Content
                  </label>
                  <textarea
                    value={testimonial.testimonialContent}
                    onChange={(e) =>
                      handleTestimonialChange(index, "testimonialContent", e.target.value)
                    }
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 resize-none"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* --- Save Button --- */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg p-4 flex items-center justify-between z-50">
  <p className="text-slate-600 font-medium">
    {status.message || "All changes are saved locally until you click Save."}
  </p>
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i> Saving...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <i className="ri-save-line"></i> Save Testimonials
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}
